
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { TaskIcon } from '@/shared/config';
import { useAuthStore } from '@/features/auth';
import {
  apiErrorText,
  formatWorkMinutes,
  tasksApi,
  ticketsApi,
  type Task,
  type TaskStatus,
} from '@/shared/api';
import { dayText, ETA_SOURCE_LABEL, type TicketDevelopment, type TicketPlan } from '@/entities/ticket';
import { TERMINAL_TICKET_STATUSES } from '@/shared/config';
import { Callout, DatePicker, Field, Input, Tooltip, toast } from '@/shared/ui';
import { cn } from '@/shared/lib/cn';
import { HoursBreakdown, type HoursRow } from '@/widgets/HoursBreakdown';
import { DevelopmentProgress } from '@/widgets/DevelopmentProgress';
import { isHandedOff } from '@/pages/Sprints/task-flow';
import { hoursOnly, parseHours } from '@/pages/Sprints/work-units';
import td from './TicketDetail.module.css';
import styles from './TasksSection.module.css';

interface Props {
  ticketId: string;
  ticketStatus?: string;
  ticketFinal?: boolean;
  plan?: TicketPlan | null;
  development?: TicketDevelopment | null;
  onPlanChanged?: () => void;
  onCount?: (count: number) => void;
}

function workOf(task: Task): { estimate: number | null; spent: number } {
  const sub = task.subtasks;
  if (sub && sub.total > 0 && sub.estimateMinutes != null) {
    return { estimate: sub.estimateMinutes || null, spent: sub.spentMinutes ?? 0 };
  }
  return {
    estimate: task.estimateWorkMinutes ?? null,
    spent: task.spentManualMinutes ?? task.spentWorkMinutes ?? 0,
  };
}

export function TasksSection({ ticketId, ticketStatus, ticketFinal, plan, development, onPlanChanged, onCount }: Props) {
  const canSee = useAuthStore((s) => s.hasPermission)('view_sprints');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [statuses, setStatuses] = useState<TaskStatus[]>([]);

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    void Promise.all([tasksApi.list({ ticketId }), tasksApi.statuses()])
      .then(([list, dict]) => {
        if (!alive) return;
        setTasks(list);
        setStatuses(dict);
        onCount?.(list.length);
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [ticketId, canSee, plan?.tasks.estimateMinutes, plan?.tasks.spentMinutes, plan?.tasks.count]);

  const hasTasks = canSee && tasks.length > 0;
  const planEmpty = !!plan && !plan.editable && plan.workMinutes == null && plan.dueOn == null;
  if (!hasTasks && (!plan || planEmpty)) return null;

  const statusOf = (key: string) => statuses.find((s) => s.key === key);
  const handedOff = tasks.filter((t) => isHandedOff(statusOf(t.status))).length;
  const allDone = hasTasks && handedOff === tasks.length;
  const ticketClosed = ticketFinal ?? (!!ticketStatus && TERMINAL_TICKET_STATUSES.includes(ticketStatus));
  const stillOpen = hasTasks && ticketClosed ? tasks.length - handedOff : 0;
  const fullCards = tasks.every((t) => t.visibility !== 'linked');

  const rows: HoursRow[] = tasks.map((task) => {
    const st = statusOf(task.status);
    const work = workOf(task);
    const sub = task.subtasks;
    return {
      id: task.id,
      label: task.key,
      title: task.title,
      estimate: work.estimate,
      spent: work.spent,
      done: isHandedOff(st),
      statusLabel: st?.label ?? task.status,
      statusColor: st?.color ?? null,
      href: `/sprints?task=${task.id}`,
      meta: sub && sub.total > 0 ? `подзадачи ${sub.done} из ${sub.total}` : undefined,
      assignees: task.assignees,
    };
  });

  return (
    <section className={cn(td.card, styles.stack)}>
      <h2 className={styles.title}>
        <TaskIcon size={16} aria-hidden />
        Разработка
        { }
        {hasTasks && (
          <span className={cn(styles.counter, allDone && styles.counterDone)}>
            {handedOff} из {tasks.length}
          </span>
        )}
      </h2>

      {plan && <PlanFields ticketId={ticketId} plan={plan} onSaved={onPlanChanged} />}

      { }
      {hasTasks && fullCards && (
        <HoursBreakdown
          plan={plan?.workMinutes ?? null}
          planLabel="План"
          totalLabel="Нарезано"
          overLabel="сверх плана"
          rows={rows}
        />
      )}
      {plan && plan.tasks.count > 0 && !(hasTasks && fullCards) && (
        <p className={styles.planSummary}>
          нарезано <strong>{formatWorkMinutes(plan.tasks.estimateMinutes)}</strong> · потрачено{' '}
          <strong>{formatWorkMinutes(plan.tasks.spentMinutes)}</strong>
          {plan.tasks.withoutEstimate > 0 && ` · без оценки: ${plan.tasks.withoutEstimate}`}
        </p>
      )}

      {hasTasks && !fullCards && (
        <ul className={styles.list}>
          {tasks.map((task) => {
            const st = statusOf(task.status);
            return (
              <li key={task.id} className={styles.row}>
                <span className={styles.number}>{task.key}</span>
                { }
                <Link
                  to={`/sprints?task=${task.id}`}
                  className={cn(styles.name, st?.isDone && styles.nameDone)}
                  title={task.title}
                >
                  {task.title}
                </Link>
                <span className={styles.status}>
                  <span className={cn(styles.dot, styles[`dot${st?.color ?? 'neutral'}`])} aria-hidden />
                  {st?.label ?? task.status}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {stillOpen > 0 && (
        <Callout tone="muted">
          Заявка закрыта, а {stillOpen === 1 ? 'задача ещё на доске' : 'задачи ещё на доске'} — дальше{' '}
          {stillOpen === 1 ? 'она ведётся' : 'они ведутся'} без заявки. Закрыть или отвязать — в карточке задачи.
        </Callout>
      )}

      {development && (
        <div className={styles.preview}>
          <span className={styles.previewLabel}>Так видит заявитель</span>
          <DevelopmentProgress
            development={development}
            note={
              development.eta?.source ? (
                <span>
                  дата — {ETA_SOURCE_LABEL[development.eta.source]}
                  {development.eta.overdue && ' (прошла — заявителю «срок уточняется»)'}
                </span>
              ) : null
            }
          />
        </div>
      )}

      {allDone && (
        <p className={styles.allDone}>
          <Tooltip content="Задача закрывается своим статусом; заявку закрываете вы">
            <span className={styles.allDoneIcon} aria-hidden>
              <Check size={13} />
            </span>
          </Tooltip>
          Разработка закончила. Заявка перешла на проверку — подтвердите, что всё работает.
        </p>
      )}
    </section>
  );
}

function PlanFields({
  ticketId,
  plan,
  onSaved,
}: {
  ticketId: string;
  plan: TicketPlan;
  onSaved?: () => void;
}) {
  const [hours, setHours] = useState(plan.workMinutes != null ? hoursOnly(plan.workMinutes).replace(' ч', '') : '');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setHours(plan.workMinutes != null ? hoursOnly(plan.workMinutes).replace(' ч', '') : '');
  }, [plan.workMinutes, focused]);

  const save = async (patch: { workMinutes?: number | null; dueOn?: string | null }) => {
    try {
      await ticketsApi.setPlan(ticketId, patch);
      onSaved?.();
    } catch (err) {
      toast.error(apiErrorText(err, 'Не удалось сохранить план'));
    }
  };

  const parsed = parseHours(hours);
  const invalid = parsed.kind === 'invalid';

  if (!plan.editable) {
    const where = (plan.editableIn ?? []).map((st) => `«${st.label}»`).join(', ');
    const hasValue = plan.workMinutes != null || plan.dueOn != null;
    if (!hasValue && !where) return null;
    return (
      <p className={styles.planSummary}>
        {hasValue ? (
          <>
            план{' '}
            {plan.workMinutes != null && <strong>{formatWorkMinutes(plan.workMinutes)}</strong>}
            {plan.dueOn && (
              <>
                {' '}
                к <strong>{dayText(plan.dueOn)}</strong>
              </>
            )}
            {where && <span className={styles.planWhere}> · правится в статусах {where}</span>}
          </>
        ) : (
          <>План не задан. Его ставят в статусах {where}.</>
        )}
      </p>
    );
  }

  return (
    <div className={styles.planFields}>
      <Field label="План, часы" error={invalid ? 'Число часов, например 40' : undefined}>
        <Input
          inputMode="decimal"
          value={hours}
          placeholder="ч"
          onFocus={() => setFocused(true)}
          onChange={(e) => setHours(e.target.value)}
          onBlur={() => {
            setFocused(false);
            if (invalid) return;
            const next = parsed.kind === 'ok' ? parsed.minutes : null;
            if (next !== plan.workMinutes) void save({ workMinutes: next });
          }}
        />
      </Field>
      <Field label="Плановая дата">
        <DatePicker
          value={plan.dueOn ?? ''}
          onChange={(next) => {
            const value = next || null;
            if (value !== plan.dueOn) void save({ dueOn: value });
          }}
        />
      </Field>
    </div>
  );
}
